/**
 * Generates a human-readable pagination summary string.
 *
 * The `+` suffix means "at least this many" and belongs to **cursor**
 * pagination, where the total is only ever a lower bound. Offset-paginated
 * callers know their total exactly and pass `hasMoreBeyondKnownPages: false`,
 * so an exact 100 never renders as `100+`.
 *
 * @example
 * // Offset: exact total, on any page
 * getPaginationSummary({ startRow: 1, endRow: 10, total: 100, hasMoreBeyondKnownPages: false })
 * // => "Showing 1 - 10 of 100 results"
 *
 * @example
 * // Cursor: 50 rows discovered so far, more pages beyond
 * getPaginationSummary({ startRow: 1, endRow: 10, total: 50, hasMoreBeyondKnownPages: true })
 * // => "Showing 1 - 10 of 50+ results"
 */
export const getPaginationSummary = ({
  startRow,
  endRow,
  total,
  hasMoreBeyondKnownPages,
}: {
  startRow: number
  endRow: number
  total?: number
  /** Cursor pagination only — see the `+` note above. Not "is there a next page?". */
  hasMoreBeyondKnownPages?: boolean
}): string => {
  if (total !== undefined && total > 0) {
    const suffix = hasMoreBeyondKnownPages ? '+' : ''
    const label = total === 1 ? 'result' : 'results'
    return `Showing ${startRow} - ${endRow} of ${total}${suffix} ${label}`
  }

  if (startRow === endRow) {
    return hasMoreBeyondKnownPages ? '1 result ...' : '1 result'
  }

  return hasMoreBeyondKnownPages
    ? `Showing ${startRow} - ${endRow}+ results`
    : `Showing ${startRow} - ${endRow} results`
}
